/**
 * Content Message Service Actions
 *
 * This file contains general content message service actions.
 *
 * For backward compatibility, this class extends WhatsappToolsClientActions
 * and inherits all WhatsApp tool methods from @common/tools/whatsapp.
 */

import { MessageServiceActions } from "@common/services/messageServiceActions";
import { postMessageWithResponse, postMessageToBackgroundWithResponse } from "@common/services/post-message-to-whatsapp-functions";
import { assertWaPageBridge } from "@common/services/page-bridge-error";
// Import directly from client-actions to avoid circular dependency with index.ts
import { WhatsappToolsClientActions } from "@common/tools/whatsapp/wa-tools-client-actions";
import type { MessageInfo } from "@extension/context/page/services/message-info.types";
import type { ChatLogGroupMetadata } from "@extension/context/page/services/PageMessageServiceHandlers";
import type { SendTraceSnapshot } from "@extension/wa-communication-layer/message/send-stage-trace";
import type { Picture } from "@bt-common/interfaces/whatsapp-types";
import type { WaMessageAckEntry, WaMessageAckKeyRequest } from "@extension/wa-communication-layer/message/message-retrieval-operations";

/**
 * ContentMessageServiceActions extends WhatsappToolsClientActions to inherit
 * all WhatsApp tool methods while adding general content service actions.
 *
 * Methods here use NON-contract cmd strings (DOM scrapers, page-side utilities
 * without a WaToolContractMap entry). The fire-and-forget signals `await` the
 * post and discard the response; the few RPC-style ones narrow the unknown
 * return with a runtime guard or ?? fallback.
 */
export class ContentMessageServiceActions extends WhatsappToolsClientActions {
  // Base messaging method from MessageServiceActions
  public static postMessageWithResponse<T>(cmd: string, data: any): Promise<T | undefined> {
    return MessageServiceActions.postMessageWithResponse(cmd, data, "contentMessageService");
  }

  // ==========================================
  // General Chat Operations (non-tool)
  // ==========================================

  public static async getChatsAndContacts(): Promise<void> {
    await postMessageWithResponse({ cmd: "getChatsAndContacts" });
  }

  public static async getCurrentChat(options?: { clearInput?: boolean }): Promise<void> {
    await postMessageWithResponse({ cmd: "getCurrentChat", data: options?.clearInput });
  }

  public static async focusContact(contactId: string): Promise<void> {
    await postMessageWithResponse({ cmd: "focusContact", data: contactId });
  }

  // ==========================================
  // Message Log Operations (non-tool)
  // ==========================================

  public static async getMessageLogWAMessageAck(messageKeys: any): Promise<any[]> {
    const response = await postMessageWithResponse({ cmd: "getMessageLogWAMessageAck", data: { messageKeys } });
    return Array.isArray(response) ? response : [];
  }

  /**
   * Resolve the current WhatsApp-store ack for one or more sent messages by
   * their persisted `waMessageKey`. Routes to the page-context ack bridge
   * (`getWaMessagesAckDataByWaMessagesKeys`), which returns, per key, the FULL
   * entry `{ messageId, waMessageKey, ...MsgInfo, ack }` on a store hit or
   * `{ messageId, waMessageKey, ack:null, error }` on a miss — the scalar `ack`
   * is ALWAYS present. The client ack reconciler depends on that invariant.
   */
  public static async getWAMessageAckData(messageKeys: WaMessageAckKeyRequest[]): Promise<WaMessageAckEntry[]> {
    const response = await postMessageWithResponse({ cmd: "getWAMessageAckData", data: { messageKeys } });
    return Array.isArray(response) ? (response as WaMessageAckEntry[]) : [];
  }

  public static async checkIfReplyingToMessage(): Promise<void> {
    await postMessageWithResponse({ cmd: "checkIfReplyingToMessage" });
  }

  // ==========================================
  // Profile & User Info (non-tool)
  // ==========================================

  public static async getMyWaProfile(): Promise<void> {
    await postMessageWithResponse({ cmd: "getMyWaProfile" });
  }

  /**
   * Fetch the profile picture thumbnail for a WhatsApp id. Used by
   * ProfilePicReducer in extension mode (server mode goes through RTK
   * Query). Returns Picture which may be empty (`{}`) when WAWeb has no
   * thumbnail for the id.
   */
  public static async getProfilePicThumb(whatsappId: string): Promise<Picture> {
    const response = await postMessageWithResponse({ cmd: "getProfilePicThumb", data: whatsappId });
    return (response ?? {}) as Picture;
  }

  /**
   * Fetch per-recipient delivery/read info for a sent message. Used by
   * MessageInfoReducer in extension mode (server mode goes through RTK
   * Query). Returns null when WAWeb can't resolve the message key.
   */
  public static async getMessageInfo(waMessageKey: string): Promise<MessageInfo | null> {
    const response = await postMessageWithResponse({ cmd: "getMessageInfo", data: { waMessageKey } });
    return (response ?? null) as MessageInfo | null;
  }

  public static async getUserInfo(): Promise<void> {
    await postMessageWithResponse({ cmd: "getUserInfo" });
  }

  public static async setNewStatus(localNewStatus: string): Promise<void> {
    await postMessageWithResponse({ cmd: "setNewStatus", data: localNewStatus });
  }

  // ==========================================
  // Media & Content (non-tool)
  // ==========================================

  public static async getMedia(chatId: string): Promise<{
    cmd: string;
    mediaBlobList: any[];
    caption: string;
  }> {
    const res = await postMessageWithResponse({ cmd: "getMedia", data: { chatId } });
    const fallback = { cmd: "getMedia", mediaBlobList: [] as any[], caption: "" };
    if (res && typeof res === 'object' && 'mediaBlobList' in res) {
      return res as { cmd: string; mediaBlobList: any[]; caption: string };
    }
    return fallback;
  }

  public static async closeMediaPreview(): Promise<boolean> {
    assertWaPageBridge("closeMediaPreview");
    const res = await postMessageWithResponse({ cmd: "closeMediaPreview" });
    return typeof res === 'boolean' ? res : false;
  }

  public static async getWAContactImageRequest(): Promise<void> {
    await postMessageWithResponse({ cmd: "getWAContactImageRequest" });
  }

  public static async getPollData(): Promise<void> {
    await postMessageWithResponse({ cmd: "getPollData" });
  }

  // ==========================================
  // WhatsApp UI Actions (non-tool)
  // ==========================================

  public static async clearChatInput(currentChatId?: string): Promise<void> {
    await postMessageWithResponse({ cmd: "clearChatInput", data: currentChatId });
  }

  public static async getWaVersion(): Promise<string | undefined> {
    assertWaPageBridge("getWaVersion");
    const res = await postMessageWithResponse({ cmd: "getWaVersion" });
    return typeof res === 'string' ? res : undefined;
  }

  public static async getWaMeUser(): Promise<string | undefined> {
    assertWaPageBridge("getWaMeUser");
    const res = await postMessageWithResponse({ cmd: "getWaMeUser" });
    return typeof res === 'string' ? res : undefined;
  }

  /**
   * Resolve group/community send-log metadata for a chat (#send-log-metadata).
   * The lookup runs in the PAGE context (needs window.WWebJSBt.getChat); this
   * content-side action is just the IPC round-trip. Returns `{}` for non-group
   * chats or on any failure — it must never break or slow the send.
   */
  public static async getChatLogMetadata(chatId: string): Promise<ChatLogGroupMetadata> {
    const res = await postMessageWithResponse({ cmd: "getChatLogMetadata", data: { chatId } });
    return (res && typeof res === 'object' ? res : {}) as ChatLogGroupMetadata;
  }

  /**
   * Pull a send stage-trace snapshot from the PAGE context
   * (#send-timeout-observability). Called by the send thunk when the 50s guard
   * fires, to record WHERE the send got stuck in the durable messagelog row.
   * Returns null when no trace exists; must never throw into the send path.
   * #503: `startedAt` pins one attempt (see getSendTrace in send-stage-trace).
   */
  public static async getSendTrace(userMessageId: string, startedAt?: number): Promise<SendTraceSnapshot | null> {
    const res = await postMessageWithResponse({ cmd: "getSendTrace", data: { userMessageId, startedAt } });
    return (res && typeof res === 'object' ? res : null) as SendTraceSnapshot | null;
  }

  /** #474: WA reject reason tokens for a send that errored after hand-off; null when none were observed. */
  public static async getSendRejectReason(chatId: string, sinceMs: number): Promise<string | null> {
    const res = await postMessageWithResponse({ cmd: "getSendRejectReason", data: { chatId, sinceMs } });
    return typeof res === 'string' && res.length > 0 ? res : null;
  }

  // ==========================================
  // External Services (non-tool)
  // ==========================================

  public static async fetchUrlContent(url: string): Promise<string> {
    return (await postMessageToBackgroundWithResponse<string>({ cmd: "fetchUrlContent", data: url })) ?? "";
  }

  public static async fetchUrlContentAsBlob(url: string): Promise<string> {
    const response: any = await postMessageToBackgroundWithResponse({ cmd: "fetchUrlContentAsBlob", data: url });
    if (response && response.error) {
      throw new Error(response.error);
    }
    return response;
  }

  /** HTTP status of `url` as the background worker sees it (0 when the request failed). */
  public static async probeUrlStatus(url: string): Promise<number> {
    const response = await postMessageToBackgroundWithResponse<{ status: number }>({ cmd: "probeUrlStatus", data: url });
    return response?.status ?? 0;
  }

  public static async openTab(boardURL: string): Promise<void> {
    await postMessageWithResponse({ cmd: "openTab", data: boardURL });
  }
}
